/* Face recognition utilities (client-side) */

// Minimal types to avoid external type deps
export type FaceBox = { x: number; y: number; width: number; height: number };
export type FaceDescriptor = number[]; // 128-length
export type Detector = 'tiny' | 'ssd';
export type DetectorOptions = { detector?: Detector; inputSize?: number; scoreThreshold?: number; strict?: boolean };

let faceapiRef: any | null = null;
let modelsLoaded = false;
let ssdLoaded = false;

export async function ensureFaceApiLoaded(modelBasePath: string = '/models', loadSSD = false) {
  if (modelsLoaded && faceapiRef) {
    if (loadSSD && !ssdLoaded) {
      try { await faceapiRef.nets.ssdMobilenetv1.loadFromUri(modelBasePath); ssdLoaded = true; } catch {}
    }
    return faceapiRef;
  }
  try {
    const tf = await import('@tensorflow/tfjs');
    // Prefer WebGL if available for better perf
    try { await import('@tensorflow/tfjs-backend-webgl'); } catch {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fa: any = await import('@vladmandic/face-api');
    faceapiRef = fa;
    if (tf && (tf as any).setBackend) {
      try { await (tf as any).setBackend('webgl'); } catch {}
      try { await (tf as any).ready(); } catch {}
    }

    const { nets } = faceapiRef;
    const loads: Promise<any>[] = [
      nets.tinyFaceDetector.loadFromUri(modelBasePath),
      nets.faceLandmark68Net.loadFromUri(modelBasePath),
      nets.faceRecognitionNet.loadFromUri(modelBasePath),
    ];
    if (loadSSD) {
      loads.push(nets.ssdMobilenetv1.loadFromUri(modelBasePath));
    }
    await Promise.all(loads);
    modelsLoaded = true;
    if (loadSSD) ssdLoaded = true;
    return faceapiRef;
  } catch (e) {
    // If models or libs are missing, propagate to caller
    throw e;
  }
}

export async function computeFaceDescriptorsFromImageElement(imgEl: HTMLImageElement, opts: DetectorOptions = {}): Promise<{ descriptors: FaceDescriptor[]; boxes: FaceBox[] }>{
  if (!faceapiRef) throw new Error('face-api not loaded');
  const detector = opts.detector ?? 'tiny';
  const inputSize = opts.inputSize ?? 416;
  const score = opts.scoreThreshold ?? (detector === 'tiny' ? 0.3 : 0.5);
  // Keep detector threshold moderate and do strictness in post-filtering to avoid missing candidates entirely
  const detScore = score;
  const detOptions = detector === 'ssd'
    ? new faceapiRef.SsdMobilenetv1Options({ minConfidence: detScore })
    : new faceapiRef.TinyFaceDetectorOptions({ inputSize, scoreThreshold: detScore });

  let source: HTMLImageElement | HTMLCanvasElement = imgEl;
  const MAX_DIM = 1024;
  if ((imgEl.width || 0) > MAX_DIM || (imgEl.height || 0) > MAX_DIM) {
    const rw = (imgEl.width || 1);
    const rh = (imgEl.height || 1);
    const scale = Math.min(MAX_DIM / rw, MAX_DIM / rh);
    const cw = Math.max(1, Math.round(rw * scale));
    const ch = Math.max(1, Math.round(rh * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(imgEl, 0, 0, cw, ch);
      source = canvas;
    }
  }

  const detections = await faceapiRef
    .detectAllFaces(source, detOptions)
    .withFaceLandmarks()
    .withFaceDescriptors();

  const srcW = (source as any).width || imgEl.width || 0;
  const srcH = (source as any).height || imgEl.height || 0;
  const frac = detector === 'ssd' ? (opts.strict ? 0.06 : 0.04) : (opts.strict ? 0.05 : 0.04);
  const minSide = Math.max(32, Math.round(Math.min(srcW, srcH) * frac));
  const minScore = detector === 'ssd' ? Math.max(opts.strict ? 0.8 : 0.7, score) : Math.max(opts.strict ? 0.55 : 0.5, score);
  const filtered = detections.filter((d: any) => {
    const bw = d?.detection?.box?.width ?? 0;
    const bh = d?.detection?.box?.height ?? 0;
    const sc = d?.detection?.score ?? 0;
    const ar = bh > 0 ? (bw / bh) : 0;
    const arMin = opts.strict ? 0.7 : 0.6;
    const arMax = opts.strict ? 1.4 : 1.6;
    const lm = d?.landmarks;
    let geoOk = true;
    if (lm && typeof lm.getLeftEye === 'function' && typeof lm.getRightEye === 'function' && typeof lm.getNose === 'function' && typeof lm.getMouth === 'function') {
      const leftEye = lm.getLeftEye() || [];
      const rightEye = lm.getRightEye() || [];
      const nose = lm.getNose() || [];
      const mouth = lm.getMouth() || [];
      const center = (pts: any[]) => {
        let sx = 0, sy = 0;
        for (let i = 0; i < pts.length; i++) { sx += pts[i].x || 0; sy += pts[i].y || 0; }
        const n = Math.max(1, pts.length);
        return { x: sx / n, y: sy / n };
      };
      if (leftEye.length && rightEye.length) {
        const le = center(leftEye);
        const re = center(rightEye);
        const eyeDist = Math.hypot((le.x - re.x), (le.y - re.y));
        const minEye = Math.min(bw, bh) * (opts.strict ? 0.24 : 0.18);
        const eyeRatio = eyeDist / Math.max(1, bw);
        const maxEye = opts.strict ? 0.6 : 0.7;
        if (eyeDist < minEye || eyeRatio > maxEye) geoOk = false;
        // Eyes should be roughly horizontal (limit roll)
        const tilt = Math.abs(Math.atan2(le.y - re.y, le.x - re.x));
        if (tilt > (opts.strict ? 0.45 : 0.7)) geoOk = false;
        const noseC = nose.length ? center(nose) : null;
        const mouthC = mouth.length ? center(mouth) : null;
        if (noseC && mouthC) {
          // vertical ordering: eyes above nose, mouth below nose
          const eyesMidY = (le.y + re.y) / 2;
          if (!(eyesMidY < noseC.y && mouthC.y > noseC.y)) geoOk = false;
          if ((mouthC.y - noseC.y) < bh * (opts.strict ? 0.1 : 0.08)) geoOk = false;
          // eyes should be in upper portion of the box
          const eyesPos = (eyesMidY - (d.detection.box.y || 0)) / Math.max(1, bh);
          if (eyesPos > (opts.strict ? 0.6 : 0.7)) geoOk = false;
          // mouth width plausibility
          const mouthMinX = mouth.reduce((m: number, p: any) => Math.min(m, p.x || m), Number.POSITIVE_INFINITY);
          const mouthMaxX = mouth.reduce((m: number, p: any) => Math.max(m, p.x || m), 0);
          const mouthW = Math.max(0, mouthMaxX - mouthMinX);
          const mouthRatio = mouthW / Math.max(1, bw);
          if (mouthRatio < (opts.strict ? 0.25 : 0.2) || mouthRatio > 0.9) geoOk = false;
        }
      }
    }
    return bw >= minSide && bh >= minSide && sc >= minScore && ar >= arMin && ar <= arMax && geoOk;
  });
  let candidates: any[] = filtered;
  if (detector === 'ssd' && opts.strict) {
    const tinyOpts = new faceapiRef.TinyFaceDetectorOptions({ inputSize: opts.strict ? 416 : 384, scoreThreshold: Math.max(opts.strict ? 0.7 : 0.65, score) });
    const tinyDet = await faceapiRef.detectAllFaces(source, tinyOpts);
    const tboxes = (tinyDet || []).map((td: any) => ({ x: td.box.x, y: td.box.y, width: td.box.width, height: td.box.height }));
    const iou = (a: any, b: any) => {
      const ax1 = a.x, ay1 = a.y, ax2 = a.x + a.width, ay2 = a.y + a.height;
      const bx1 = b.x, by1 = b.y, bx2 = b.x + b.width, by2 = b.y + b.height;
      const ix1 = Math.max(ax1, bx1), iy1 = Math.max(ay1, by1), ix2 = Math.min(ax2, bx2), iy2 = Math.min(ay2, by2);
      const iw = Math.max(0, ix2 - ix1), ih = Math.max(0, iy2 - iy1);
      const inter = iw * ih;
      const union = a.width * a.height + b.width * b.height - inter || 1;
      return inter / union;
    };
    const minIou = 0.35;
    candidates = filtered.filter((d: any) => tboxes.some((tb: { x: number; y: number; width: number; height: number }) => iou(tb, d.detection.box) >= minIou));
  }

  // Fallback: if strict mode produced zero candidates, re-apply relaxed filtering on the same detections
  if (opts.strict && candidates.length === 0) {
    const frac2 = (detector === 'ssd' ? 0.06 : 0.05);
    const minSide2 = Math.max(32, Math.round(Math.min(srcW, srcH) * frac2));
    const minScore2 = detector === 'ssd' ? Math.max(0.7, score) : Math.max(0.5, score);
    const arMin2 = 0.6, arMax2 = 1.6;
    const filtered2 = detections.filter((d: any) => {
      const bw = d?.detection?.box?.width ?? 0;
      const bh = d?.detection?.box?.height ?? 0;
      const sc = d?.detection?.score ?? 0;
      const ar = bh > 0 ? (bw / bh) : 0;
      let geoOk = true;
      const lm = d?.landmarks;
      if (lm && typeof lm.getLeftEye === 'function' && typeof lm.getRightEye === 'function' && typeof lm.getNose === 'function' && typeof lm.getMouth === 'function') {
        const leftEye = lm.getLeftEye() || [];
        const rightEye = lm.getRightEye() || [];
        if (leftEye.length && rightEye.length) {
          const center = (pts: any[]) => {
            let sx = 0, sy = 0; for (let i = 0; i < pts.length; i++) { sx += pts[i].x || 0; sy += pts[i].y || 0; }
            const n = Math.max(1, pts.length); return { x: sx / n, y: sy / n };
          };
          const le = center(leftEye); const re = center(rightEye);
          const eyeDist = Math.hypot((le.x - re.x), (le.y - re.y));
          if (eyeDist < Math.min(bw, bh) * 0.18) geoOk = false;
        }
      }
      return bw >= minSide2 && bh >= minSide2 && sc >= minScore2 && ar >= arMin2 && ar <= arMax2 && geoOk;
    });
    if (detector === 'ssd') {
      const tinyOpts2 = new faceapiRef.TinyFaceDetectorOptions({ inputSize: 384, scoreThreshold: Math.max(0.65, score) });
      const tinyDet2 = await faceapiRef.detectAllFaces(source, tinyOpts2);
      const tboxes2 = (tinyDet2 || []).map((td: any) => ({ x: td.box.x, y: td.box.y, width: td.box.width, height: td.box.height }));
      const iou = (a: any, b: any) => {
        const ax1 = a.x, ay1 = a.y, ax2 = a.x + a.width, ay2 = a.y + a.height;
        const bx1 = b.x, by1 = b.y, bx2 = b.x + b.width, by2 = b.y + b.height;
        const ix1 = Math.max(ax1, bx1), iy1 = Math.max(ay1, by1), ix2 = Math.min(ax2, bx2), iy2 = Math.min(ay2, by2);
        const iw = Math.max(0, ix2 - ix1), ih = Math.max(0, iy2 - iy1);
        const inter = iw * ih; const union = a.width * a.height + b.width * b.height - inter || 1; return inter / union;
      };
      const minIou2 = 0.3;
      candidates = filtered2.filter((d: any) => tboxes2.some((tb: { x: number; y: number; width: number; height: number }) => iou(tb, d.detection.box) >= minIou2));
    } else {
      candidates = filtered2;
    }
  }

  const descriptors: FaceDescriptor[] = candidates.map((d: any) => Array.from(d.descriptor as Float32Array));
  const boxes: FaceBox[] = candidates.map((d: any) => ({
    x: d.detection.box.x,
    y: d.detection.box.y,
    width: d.detection.box.width,
    height: d.detection.box.height,
  }));
  return { descriptors, boxes };
}

export async function computeFaceDescriptorsFromBlob(blob: Blob, opts: DetectorOptions = {}): Promise<{ descriptors: FaceDescriptor[]; boxes: FaceBox[] }>{
  const url = URL.createObjectURL(blob);
  try {
    const img = await imageFromUrl(url);
    return await computeFaceDescriptorsFromImageElement(img, opts);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function imageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export function euclideanDistance(a: FaceDescriptor, b: FaceDescriptor): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    sum += diff * diff;
  }
  return Math.sqrt(sum);
}

export function isMatch(a: FaceDescriptor, b: FaceDescriptor, threshold = 0.6): boolean {
  return euclideanDistance(a, b) <= threshold;
}
